import { useMemo, useCallback } from 'react';
import useContactData from './useContactData';
import usePreferencesData from './usePreferencesData';

const useUserData = () => {
  // Utiliser les deux hooks en parallèle
  const contact = useContactData();
  const preferences = usePreferencesData();

  // Données combinées
  const data = useMemo(() => ({
    contact: contact.data,
    preferences: preferences.data
  }), [contact.data, preferences.data]);

  // États de chargement combinés
  const loading = useMemo(() => ({
    contact: contact.loading,
    preferences: preferences.loading,
    saving: contact.saving || preferences.saving
  }), [contact.loading, contact.saving, preferences.loading, preferences.saving]);

  // Erreurs combinées
  const errors = useMemo(() => ({
    contact: contact.error,
    preferences: preferences.error,
    saving: contact.savingError || preferences.savingError
  }), [contact.error, contact.savingError, preferences.error, preferences.savingError]);

  // Fonctions de mise à jour
  const updateContactData = contact.updateData;
  const updatePreferencesData = preferences.updateData;

  // Fonctions utilitaires combinées
  const refreshData = useCallback(async () => {
    await Promise.all([
      contact.refreshData(),
      preferences.refreshData()
    ]);
  }, [contact.refreshData, preferences.refreshData]);

  const invalidateCache = useCallback(() => {
    contact.invalidateCache();
    preferences.invalidateCache();
  }, [contact.invalidateCache, preferences.invalidateCache]);

  const saveData = useCallback(async () => {
    await Promise.all([
      contact.saveData(),
      preferences.saveData()
    ]);
  }, [contact.saveData, preferences.saveData]);

  // État d'authentification (les deux hooks devraient avoir le même état)
  const isAuthenticated = contact.isAuthenticated && preferences.isAuthenticated;

  // Indicateurs de changements en attente
  const hasUnsavedChanges = contact.hasUnsavedChanges || preferences.hasUnsavedChanges;

  return {
    // Données
    data,
    
    // États de chargement et erreurs
    loading,
    errors,
    
    // Fonctions de mise à jour
    updateContactData,
    updatePreferencesData,
    
    // Fonctions utilitaires
    refreshData,
    invalidateCache,
    saveData,
    
    // État d'authentification
    isAuthenticated,
    
    // Indicateurs de changements en attente
    hasUnsavedChanges,

    // Accès direct aux hooks individuels si nécessaire
    contact,
    preferences
  };
};

export default useUserData;